import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/api/client';
import { fetchMembers, fetchRoles, removeMember, updateMemberRole } from '@/api/rbac';
import { PermissionGate } from '@/components/PermissionGate';
import { useAuth } from '@/features/auth/useAuth';
import { usePermissions } from '@/features/auth/usePermissions';
import type { Member } from '@/types/api';

export function MembersPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { can } = usePermissions();
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({ queryKey: ['members'], queryFn: fetchMembers });
  const roles = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['members'] });
  };

  const changeRole = useMutation({
    mutationFn: ({ id, roleId }: { id: string; roleId: string }) => updateMemberRole(id, roleId),
    onSuccess: invalidate,
    onError: (e) =>
      setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Could not change the role.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeMember(id),
    onSuccess: invalidate,
    onError: (e) =>
      setError(e instanceof ApiError ? (e.detail ?? e.title) : 'Could not remove the member.'),
  });

  if (members.isPending || roles.isPending) {
    return <CircularProgress size={24} />;
  }

  if (members.error) {
    return <Alert severity="error">You do not have access to the member list.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h1">Members</Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {members.data?.data.map((member: Member) => {
              // The server refuses either way; disabling here just avoids
              // offering an action that would be rejected.
              const isSelf = member.user.id === user?.id;

              return (
                <TableRow key={member.id} hover>
                  <TableCell>{member.user.name}</TableCell>
                  <TableCell>{member.user.email}</TableCell>
                  <TableCell>
                    {can('member:update') && !isSelf ? (
                      <Select
                        size="small"
                        value={member.role_id}
                        disabled={changeRole.isPending}
                        onChange={(e) =>
                          changeRole.mutate({ id: member.id, roleId: e.target.value })
                        }
                        sx={{ minWidth: 160 }}
                      >
                        {roles.data?.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip size="small" label={member.role_label ?? '—'} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={member.status === 'active' ? 'success' : 'warning'}
                      label={member.status}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <PermissionGate permission="member:remove">
                      <Tooltip title={isSelf ? 'You cannot remove yourself' : 'Remove member'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isSelf || remove.isPending}
                            onClick={() => remove.mutate(member.id)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </PermissionGate>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </Stack>
  );
}
